<script setup lang="ts">
import { ref, onMounted, getCurrentInstance } from 'vue';
import MainContentPanel from '@/components/display/general/MainContentPanel.vue';
import HeadingSecondary from '@/components/general/HeadingSecondary.vue';
import UserDocuments from '@/components/user/UserDocuments.vue';
import BreadCrumb from '@/components/general/BreadCrumb.vue';
import ResourceLoadStatus from '@/components/general/ResourceLoadStatus.vue';
import { useState, useActions } from '@/composables/useVuexHelpers';
import { xhr } from '@/xhr/endpoints';
import { errors } from '@/errors/errors';

interface User {
  _id: string;
  name: string;
  emails: string[];
  globalRoles?: string[];
  active?: boolean;
  documents?: unknown[];
}

const instance = getCurrentInstance();

const { idToken } = useState(['idToken']);
const { impersonateUser } = useActions(['impersonateUser']);

const user = ref<User | null>(null);
const loading = ref(false);
const notAuthorized = ref(false);
const notFound = ref(false);
const unknownError = ref(false);

const toggleActive = async () => {
  if (user.value) {
    user.value = await xhr.patchUser({ active: !user.value.active }, user.value._id, idToken.value);
  }
};

const onAdminNavigate = (path: string) => {
  if (user.value && (!user.value.globalRoles || !user.value.globalRoles.includes('superadmin'))) {
    impersonateUser({ userId: user.value._id, path });
  }
};

onMounted(async () => {
  loading.value = true;
  try {
    const userId = instance?.proxy?.$route?.params?.userId as string;
    user.value = await xhr.getUser(userId, idToken.value);
  } catch (e) {
    if (e instanceof errors.UnauthorizedError) {
      notAuthorized.value = true;
    } else if (e instanceof errors.NotFoundError) {
      notFound.value = true;
    } else {
      unknownError.value = true;
    }
  }
  loading.value = false;
});
</script>

<template>
  <div>
    <BreadCrumb
      :path="[
        {
          title: 'Users',
          route: '/users-admin',
        },
        {
          title: user.name,
          route: `/user-admin/${user._id}`,
        },
      ]"
    />
    <MainContentPanel title="User" buttonIcon="users">
      <ResourceLoadStatus
        v-if="loading || notFound || notAuthorized || unknownError"
        :loading="loading"
        :not-found="notFound"
        :unauthorized="notAuthorized"
        :unknownError="unknownError"
      />
      <div v-else-if="user">
        <form class="uk-flex uk-flex-column">
          <form-group class="uk-margin">
            <span class="uk-inline">
              Name:
              <input
                id="user-name"
                class="uk-input"
                type="text"
                v-model="user.name"
                required
                :disabled="true"
              />
            </span>
          </form-group>
          <form-group class="uk-margin">
            <span class="uk-inline">
              Emails:
              <template v-for="email in user.emails" :key="email">
                <div>{{ email }}</div>
              </template>
            </span>
          </form-group>
          <form-group class="uk-margin">
            <span class="uk-inline">
              Global Roles:
              <template v-for="role in user.globalRoles" :key="role">
                <div>{{ role }}</div>
              </template>
            </span>
          </form-group>

          <div
            v-if="
              user &&
              typeof user.active === 'boolean' &&
              (!user.globalRoles || !user.globalRoles.includes('superadmin'))
            "
            class="uk-margin"
          >
            <button
              class="uk-button uk-button-default uk-button-primary uk-border-rounded"
              @click.prevent="toggleActive()"
            >
              {{ user.active ? 'Deactivate' : 'Activate' }}
            </button>
            <button
              class="uk-button uk-button-default uk-button-danger uk-margin-left uk-border-rounded"
              @click.prevent="
                impersonateUser({
                  userId: user._id,
                  path: '/discussions',
                })
              "
            >
              Impersonate
            </button>
          </div>
        </form>

        <HeadingSecondary title="User Discussions" :small="true" />
        <UserDocuments
          :documents="user.documents"
          :admin-mode="true"
          @adminModeNavigate="onAdminNavigate"
        ></UserDocuments>
      </div>
    </MainContentPanel>
  </div>
</template>
